<template>
  <Flex direction="column">
    <Flex
      v-if="showHouseNumber"
      :direction="$screen === 's' ? 'column' : 'row'"
      equalWidth
    >
      <AddressSearchInput
        v-model="street"
        :required="required"
        :testId="testId"
        @select="onSelectSuggestion"
        @update:notFound="$emit('update:notFound', $event)"
      />
      <BaseInput
        v-model="houseNumber"
        :label="$t('global.house_number')"
        v-test="`${testId}-houseNumber`"
      />
    </Flex>
    <AddressSearchInput
      v-else
      v-model="addressDisplay"
      :required="required"
      :testId="testId"
      @select="onSelectSuggestion"
      @update:notFound="$emit('update:notFound', $event)"
    />
    <Flex
      :direction="$screen === 's' ? 'column' : 'row'"
      equalWidth
    >
      <BaseInput
        v-model="zipcode"
        :required="required"
        :label="$t('global.postal_code')"
        v-test="`${testId}-zipcode`"
      />
      <BaseInput
        v-model="city"
        :required="required"
        :label="$t('global.city')"
        v-test="`${testId}-city`"
      />
    </Flex>
  </Flex>
</template>

<script
  lang="ts"
  setup
>
import { useLazyQuery } from '@vue/apollo-composable';
import { computed, ref, watch } from 'vue';
import AddressSearchInput from './AddressSearchInput.vue';
import { combineAddress, splitAddress } from './address-parsing';
import { ADDRESS_SUGGESTION_DETAILS } from './graphql';

const props = withDefaults(
  defineProps<{
    address?: string;
    // When showHouseNumber is set, the house number gets its own field next to the address
    // instead of the default of combining it into the address string - `address` then holds
    // only the street, and houseNumber is read/emitted as its own field.
    houseNumber?: string;
    showHouseNumber?: boolean;
    zipcode?: string;
    city?: string;
    required?: boolean;
    testId?: string;
  }>(),
  {
    address: '',
    houseNumber: '',
    showHouseNumber: false,
    zipcode: '',
    city: '',
    required: false,
    testId: 'customer',
  },
);

const emit = defineEmits<{
  (e: 'update:address', value: string): void;
  (e: 'update:houseNumber', value: string): void;
  (e: 'update:zipcode', value: string): void;
  (e: 'update:city', value: string): void;
  (e: 'update:notFound', value: boolean): void;
}>();

const street = ref('');
const houseNumber = ref('');
const numberFirst = ref(false);

const applySplit = (value: string) => {
  const result = splitAddress(value, numberFirst.value);
  street.value = result.street;
  houseNumber.value = result.houseNumber;
  numberFirst.value = result.numberFirst;
};

if (props.showHouseNumber) {
  street.value = props.address;
  houseNumber.value = props.houseNumber;
} else {
  applySplit(props.address);
}

watch(
  () => props.address,
  (value) => {
    if (props.showHouseNumber) {
      if (value !== street.value) {
        street.value = value;
      }
      return;
    }

    if (
      value !==
      combineAddress(street.value, houseNumber.value, numberFirst.value)
    ) {
      applySplit(value);
    }
  },
);

if (props.showHouseNumber) {
  watch(
    () => props.houseNumber,
    (value) => {
      if (value !== houseNumber.value) {
        houseNumber.value = value;
      }
    },
  );
}

// numberFirst is watched too since a re-lookup can flip it without street/houseNumber changing.
watch(
  [street, houseNumber, numberFirst],
  ([newStreet, newHouseNumber, isNumberFirst]) => {
    if (props.showHouseNumber) {
      emit('update:address', newStreet);
      emit('update:houseNumber', newHouseNumber);
      return;
    }

    emit(
      'update:address',
      combineAddress(newStreet, newHouseNumber, isNumberFirst),
    );
  },
);

const zipcode = computed({
  get: () => props.zipcode,
  set: (value) => emit('update:zipcode', value),
});

const city = computed({
  get: () => props.city,
  set: (value) => emit('update:city', value),
});

// The address field doubles as the search box - it displays street + house number combined
// (matching how the address is stored). Picking a suggestion resolves the rest below.
const addressDisplay = computed({
  get: () => combineAddress(street.value, houseNumber.value, numberFirst.value),
  set: (value: string) => {
    const result = splitAddress(value, numberFirst.value);
    street.value = result.street;
    houseNumber.value = result.houseNumber;
    numberFirst.value = result.numberFirst;
  },
});

const {
  load: loadSuggestionDetails,
  refetch: refetchSuggestionDetails,
  onResult: onSuggestionDetailsResult,
} = useLazyQuery(ADDRESS_SUGGESTION_DETAILS);

onSuggestionDetailsResult(({ data }) => {
  const details = data?.addressSuggestionDetails;

  if (!details?.street && !details?.city) {
    emit('update:notFound', true);

    return;
  }

  emit('update:notFound', false);
  numberFirst.value = !!details.numberFirst;
  street.value = details.street ?? street.value;
  houseNumber.value = details.houseNumber ?? houseNumber.value;

  if (details.city) {
    city.value = details.city;
  }

  if (details.zipcode) {
    emit('update:zipcode', details.zipcode);
  }
});

const onSelectSuggestion = (id: string) => {
  emit('update:notFound', false);

  const variables = { id };

  loadSuggestionDetails(undefined, variables) ||
    refetchSuggestionDetails(variables);
};
</script>
